import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCampuses } from '../../contexts/CampusContext';
import { useEvents } from '../../contexts/EventsContext';
import { useLeaders } from '../../contexts/LeaderContext';
import { useMinistries } from '../../contexts/MinistryContext';
import MinistryLayout from './MinistryLayout';

const ChildrenMinistry = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ministryId = queryParams.get('ministryId');
  const {leaders} = useLeaders();
  const {campuses} = useCampuses();
  const {ministries} = useMinistries();
  const [ministryLeaders, setMinistryLeaders] = useState([]);
  const [ministry, setMinistry] = useState({});
  const [ministryEvents, setMinistryEvents] = useState([]);
  const {events} = useEvents();

  useEffect(() => {
    if (leaders && campuses && ministries) {
      // Enhance leaders with campus names
      const enhancedLeaders = leaders
        .filter(leader => leader.ministryId === ministryId)
        .map(leader => {
          // Find the campus for this leader
          const campus = campuses.find(c => c.id === leader.campusId);
          // Return new object with campusName added
          return {
            ...leader,
            campusName: campus ? campus.name : 'Unknown Campus'
          };
        });
      
      setMinistryLeaders(enhancedLeaders);
      
      // Find the current ministry
      const currentMinistry = ministries.find(m => m.id === ministryId);
      setMinistry(currentMinistry || {});
      
    }
  }, [leaders, campuses, ministries, ministryId]);
  
useEffect(() => {
    if (events && ministry.name) {
      const filteredEvents = events.filter(event => {
        // Skip if event is missing required fields
        if (!event || !event.targetGroup) return false;
        
        // Compare with ministry name (case insensitive)
        return event.targetGroup.toLowerCase() === ministry.name.toLowerCase();
      });
      
      // Sort by date (newest first)
      const sortedEvents = filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setMinistryEvents(sortedEvents);
    } else {
      setMinistryEvents([]);
    }
  }, [events, ministry.name]);
  
  return (
    <MinistryLayout
      title="Children's Ministry"
      description="Nurturing the faith of children through fun, biblical teaching, and building strong foundations."
      leaders= {ministryLeaders}
      coverImage={ministry.cover_image}
      vision="To see every child know Jesus personally, grow in their understanding of God's Word, and develop a strong foundation of faith that will last a lifetime."
      mission="To partner with parents in raising children who love God, love others, and are equipped to serve Christ with their gifts and talents."
      activities={[
        "Age-appropriate Bible classes for nursery through elementary",
        "Interactive worship and prayer experiences",
        "Bible memorization program with rewards",
        "Quarterly family events and celebrations",
        "Summer Vacation Bible School",
        "Children's choir and drama team",
        "Community outreach and service projects"
      ]}
      schedules={ministryEvents}
    >
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Age Groups</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100">
            <h4 className="text-lg font-medium text-yellow-800 mb-2">Littles (Ages 0-2)</h4>
            <p className="text-gray-700">
              A nurturing environment where infants and toddlers experience God's love through caring teachers, songs, and simple Bible stories.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="text-lg font-medium text-blue-800 mb-2">Preschool (Ages 3-5)</h4>
            <p className="text-gray-700">
              Engaging activities, colorful visuals, and interactive stories help preschoolers learn biblical truths in a fun, age-appropriate way.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-100">
            <h4 className="text-lg font-medium text-green-800 mb-2">Elementary (Ages 6-11)</h4>
            <p className="text-gray-700">
              Dynamic worship, hands-on learning, and small group discussions help elementary children deepen their understanding of Scripture and apply it to life.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Current Theme</h3>
        <div className="flex flex-col md:flex-row gap-6">
          <img 
            src={`${process.env.PUBLIC_URL}/images/children-theme.jpg`} 
            alt="Children's Theme" 
            className="w-full md:w-1/3 rounded-lg object-cover h-48"
          />
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Heroes of Faith</h4>
            <p className="text-gray-700 mb-4">
              Join us as we explore the exciting stories of faith heroes in the Bible! Children will discover how ordinary people did extraordinary things with God's help, and learn how they too can be heroes of faith.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">Bible Stories</span>
              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">Character Building</span>
              <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded">Faith Development</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Parent Resources</h3>
        <p className="text-gray-700 mb-4">
          We believe parents are the primary spiritual influencers in a child's life. Here are resources to help you nurture your child's faith at home.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Weekly Take-Home Activities</h4>
            <p className="text-gray-700">
              Reinforce Sunday's lesson with fun activities, discussion questions, and Bible reading suggestions for the whole family.
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Family Devotional Guide</h4>
            <p className="text-gray-700">
              A simple 5-minute daily devotional to help your family connect with God's Word throughout the week.
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Parent Workshops</h4>
            <p className="text-gray-700">
              Quarterly workshops on topics like family discipleship, answering tough questions, and nurturing faith at different developmental stages.
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Recommended Resources</h4>
            <p className="text-gray-700">
              Curated list of books, apps, and videos to support your child's spiritual growth at every age.
            </p>
          </div>
        </div>
      </div>
    </MinistryLayout>
  );
};

export default ChildrenMinistry;
